import { Subscription } from 'rxjs';
import { Optional } from './../../../common/utils/optional';
import { ErpAdditionalMessageDto } from './../../../common/model/erpadditionalmessagedto';
import { JobApplicationAndDocuments } from './../../model/jobapplicationanddocuments';
import { JOBAPPLICATION_COMPONENT_IDENT } from 'app/rm/erp/jobapplication/model/constants';
import { JOBADVERTISEMENT_SEARCH_CONFIG_BASE_PATH, SEARCH_CONFIG_JOBADVERTISEMENT } from 'app/rm/erp/common/model/searchConstants';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { HisNavigationService, HisBaseGlobalsService, DdEntryMerged } from 'hisng';
import { JobAdvertisement } from 'app/rm/erp/jobadvertisement/model/jobadvertisement';
import { JobAdvertisementService } from 'app/rm/erp/jobadvertisement/service/jobadvertisement.service';
import { JobApplication } from '../../model/jobapplication';
import { FileUpload } from 'primeng/fileupload';
import { hasFiles } from 'app/rm/erp/common/utils/event-typeguards';

@Component( {
    selector: 'app-jobapplication-page',
    templateUrl: './jobapplication-page.component.html'
} )
export class JobApplicationPageComponent implements OnInit {

    private static readonly SEARCH_URL = JOBADVERTISEMENT_SEARCH_CONFIG_BASE_PATH + SEARCH_CONFIG_JOBADVERTISEMENT;
    private static readonly LOCAL_STORAGE_JOBAPPLICATION_KEY = 'his_rm_erp_jobapplication_create_jobapplication_';

    readonly JOBAPPLICATION_COMPONENT_IDENT = JOBAPPLICATION_COMPONENT_IDENT;

    jobApplication: JobApplicationAndDocuments = new JobApplicationAndDocuments();
    jobApplicationDto: ErpAdditionalMessageDto<JobApplication> = { dto: new JobApplication(), message: '' };

    jobAdvertisement: JobAdvertisement = new JobAdvertisement();
    activeComponent: string = JOBAPPLICATION_COMPONENT_IDENT.form;

    isRefNumberVisible = true;
    isValidToVisible = true;

    private jobAdvertisementSubscription?: Subscription;
    private jobApplicationPossibleSubscription?: Subscription;

    constructor(
        private readonly jobAdvertisementService: JobAdvertisementService,
        private readonly navigationService: HisNavigationService,
        private readonly route: ActivatedRoute,
        private readonly hisBaseGlobalsService: HisBaseGlobalsService
    ) { }

    ngOnInit(): void {
        this.route.paramMap.subscribe(
            ( params: ParamMap ) => {
                Optional.ofNullable( params.get( 'advertisementId' ) )
                    .map( id => parseInt( id, 10 ) )
                    .ifPresent( jobAdvertisementId => {
                        this.jobApplication.application.jobAdvertisementId = jobAdvertisementId;
                        this.subscribeToJobAdvertisementService( jobAdvertisementId );
                        this.loadApplicationFromLocalStorage( jobAdvertisementId );
                    } );
            }
        );

        let ddRefNumber: DdEntryMerged = this.hisBaseGlobalsService.getDd('rm.erp.jobapplication.infobox.refNumber');
        this.isRefNumberVisible = this.isVisible(ddRefNumber);

        let ddValidTo: DdEntryMerged = this.hisBaseGlobalsService.getDd('rm.erp.jobapplication.infobox.validTo');
        this.isValidToVisible = this.isVisible(ddValidTo);
    }

    jobApplicationCreated( _: never ): void {
        this.saveApplicationToLocalStorage();
        this.activeComponent = JOBAPPLICATION_COMPONENT_IDENT.overview;
    }

    jobApplicationSent( jobApplicationDto: ErpAdditionalMessageDto<JobApplication> ): void {
        this.jobApplicationDto = jobApplicationDto;
        this.removeApplicationFromLocalStorage();
        this.activeComponent = JOBAPPLICATION_COMPONENT_IDENT.success;
    }

    backToJobApplicationForm( _: never ): void {
        this.activeComponent = JOBAPPLICATION_COMPONENT_IDENT.form;
    }

    onModelChange( _: never ): void {
        this.saveApplicationToLocalStorage();
    }

    deleteJobApplication( _: never ): void {
        this.removeApplicationFromLocalStorage();
        this.jobApplication = new JobApplicationAndDocuments();
        this.jobApplication.application.jobAdvertisementId = this.jobAdvertisement.id;
    }

    localSave(): void {
        const jobApplicationJson: string = JSON.stringify(this.jobApplication.application);
        const fileName: string = this.jobAdvertisement.shortDescription + '_' + this.jobAdvertisement.id + '.json';
        this.saveFile(fileName, jobApplicationJson);
    }

    localLoad( event: unknown, fileUpload: FileUpload ): void {
        if ( !hasFiles( event ) ) {
            return;
        }
        const file = event.files[0];
        const reader: FileReader = new FileReader();
        reader.onload = ( e: any ) => {
            const json: object = JSON.parse( reader.result as string );
            const loadedApplication: JobApplicationAndDocuments = new JobApplicationAndDocuments( new JobApplication( json ), [], false );
            loadedApplication.application.id = null;
            loadedApplication.application.jobAdvertisementId = this.jobAdvertisement.id;
            this.jobApplication = loadedApplication;
        };
        reader.readAsText( file as Blob );
        fileUpload.files = [];
        fileUpload.fileLimit = 0;
        fileUpload.cd.markForCheck();
    }

    private isVisible(ddEntry: DdEntryMerged): boolean {
        if ( ddEntry && ddEntry.hidden ) {
            return false;
        }
        return true;
    }

    private saveApplicationToLocalStorage(): void {
        const key = JobApplicationPageComponent.LOCAL_STORAGE_JOBAPPLICATION_KEY + this.jobAdvertisement.id;
        const application = JSON.stringify( this.jobApplication.application );
        localStorage.setItem( key, application );
    }

    private removeApplicationFromLocalStorage(): void {
        const key = JobApplicationPageComponent.LOCAL_STORAGE_JOBAPPLICATION_KEY + this.jobAdvertisement.id;
        localStorage.removeItem( key );
    }

    private loadApplicationFromLocalStorage( jobAdvertisementId: number ): void {
        const key = JobApplicationPageComponent.LOCAL_STORAGE_JOBAPPLICATION_KEY + jobAdvertisementId;
        const item = localStorage.getItem( key );
        if ( item != null ) {
            const tempApplication: JobApplication = new JobApplication( JSON.parse( item ) );
            if ( tempApplication.jobAdvertisementId === jobAdvertisementId ) {
                this.jobApplication = new JobApplicationAndDocuments( tempApplication );
            }
        }
    }

    private subscribeToJobAdvertisementService( jobAdvertisementId: number ): void {
        if ( this.jobAdvertisementSubscription ) {
            this.jobAdvertisementSubscription.unsubscribe();
        }
        if ( this.jobApplicationPossibleSubscription ) {
            this.jobApplicationPossibleSubscription.unsubscribe();
        }

        this.jobAdvertisementSubscription = this.jobAdvertisementService
            .getById( jobAdvertisementId )
            .subscribe( ad => this.jobAdvertisement = ad );

        // Wenn eine Bewerbung auf die angegebene Stelle nicht (mehr) möglich ist, soll eine Umleitung auf die Liste aller
        // noch aktiven Stellenausschreibungen erfolgen. Dieser Fall kann insb. eintreten, wenn das Bewerbungsformular
        // für eine Stelle gebookmarkt wurde, die Stellenausschreibung jedoch in der Zwischenzeit inaktiv geworden ist.
        this.jobApplicationPossibleSubscription = this.jobAdvertisementService.checkJobApplicationPossible( jobAdvertisementId ).subscribe({
            next: ( () => { } ),
            error: () => {
                this.navigationService.setSearchResultUrl( JobApplicationPageComponent.SEARCH_URL );
                this.navigationService.navigateToLastSearchResultUrl();
            } });
    }

    private saveFile(filename: string, content: string): void {
        const dlink: HTMLAnchorElement = document.createElement('a');
        dlink.download = filename;
        dlink.href = 'data:text/plain;charset=utf-16,' + content;
        dlink.click();
        dlink.remove();
    }
}
